import type { BuyerLeadSource } from "@/api/buyers";

/**
 * The CLOSED vocabulary a mapping plan may use — a mirror of the C#
 * `BuyerImportVocabulary`.
 *
 * <p>🔴 <b>The lists are matched ORDINALLY and case-sensitively on the
 * server</b>, so these strings are the wire values and not labels. The label
 * a human reads lives beside each one; editing a VALUE here silently proposes
 * a target the server will drop to Notes with a warning (X3), which is a
 * degradation nothing on this tier can see.</p>
 *
 * <p>🔴 <b>Nothing imports across the language boundary</b>, so this mirror and
 * the C# constant can drift. Held in step here by `smartImportVocabulary.test.ts`,
 * which reads the C# source off disk — the shape the repo already uses for
 * `advertisingChannelSources` and the prequalification funnel step counts.</p>
 */

// ---------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------

/**
 * Every legal target, in the order the prompt lists them.
 *
 * `as const` so `BuyerImportTarget` is the union of the literals rather than
 * `string`: a typo in a switch over targets is then a typecheck error.
 */
export const BUYER_IMPORT_TARGETS = [
  "FirstName",
  "LastName",
  "FullName",
  "Email",
  "Phone",
  "City",
  "State",
  "Zip",
  "CityState",
  "Source",
  "SourceDetail",
  "LiquidCapital",
  "NetWorth",
  "MaxPurchasePrice",
  "DownPaymentAvailable",
  "StatedPriceBand",
  "TargetIndustries",
  "TargetGeographies",
  "TimelineToPurchase",
  "IndustryExperience",
  "BuyerType",
  "AcquiringCompanyName",
  "AcquiringCompanyTitle",
  "AcquiringCompanyWebsite",
  "inquiry.listingNumber",
  "inquiry.listingTitle",
  "inquiry.message",
  "inquiry.receivedAt",
  "inquiry.kind",
  "Notes",
  "ignore",
] as const;

export type BuyerImportTarget = (typeof BUYER_IMPORT_TARGETS)[number];

/** The per-ROW facts. They do not map to buyer fields. */
export const BUYER_IMPORT_INQUIRY_TARGETS = [
  "inquiry.listingNumber",
  "inquiry.listingTitle",
  "inquiry.message",
  "inquiry.receivedAt",
  "inquiry.kind",
] as const;

export function isInquiryTarget(target: string): boolean {
  return (BUYER_IMPORT_INQUIRY_TARGETS as readonly string[]).includes(target);
}

export function isKnownTarget(target: string): target is BuyerImportTarget {
  return (BUYER_IMPORT_TARGETS as readonly string[]).includes(target);
}

/**
 * What a reader sees in the target dropdown.
 *
 * Exhaustive over the union, so a target added to `BUYER_IMPORT_TARGETS`
 * without a label is a typecheck error rather than a dropdown entry reading
 * `inquiry.listingNumber`.
 */
export const BUYER_IMPORT_TARGET_LABELS: Record<BuyerImportTarget, string> = {
  FirstName: "First name",
  LastName: "Last name",
  FullName: "Full name (split into first + last)",
  Email: "Email",
  Phone: "Phone",
  City: "City",
  State: "State",
  Zip: "ZIP",
  CityState: "City + state (split)",
  Source: "Source",
  SourceDetail: "Source detail",
  LiquidCapital: "Liquid capital",
  NetWorth: "Net worth",
  MaxPurchasePrice: "Max purchase price (qualified)",
  DownPaymentAvailable: "Down payment available",
  StatedPriceBand: "Stated price band (self-reported budget)",
  TargetIndustries: "Target industries",
  TargetGeographies: "Target geographies",
  TimelineToPurchase: "Timeline to purchase",
  IndustryExperience: "Industry experience",
  BuyerType: "Buyer type (how they are buying)",
  AcquiringCompanyName: "Acquiring company name",
  AcquiringCompanyTitle: "Title at the acquiring company",
  AcquiringCompanyWebsite: "Acquiring company website",
  "inquiry.listingNumber": "Inquiry · listing number",
  "inquiry.listingTitle": "Inquiry · listing title",
  "inquiry.message": "Inquiry · message",
  "inquiry.receivedAt": "Inquiry · received at",
  "inquiry.kind": "Inquiry · kind",
  Notes: "Notes",
  ignore: "Ignore this column",
};

/**
 * The one target whose label would otherwise read like its neighbour's.
 *
 * <p>🔴 A column that says "Budget" is a SELF-REPORT and belongs on
 * `StatedPriceBand`. `MaxPurchasePrice` means "the amount Phoenix qualified
 * them for" and is read as exactly that by listing matching and by a
 * pipeline-value sum, so a self-report written there inflates a reported
 * dollar figure against a number nobody verified.</p>
 */
export const MAX_PURCHASE_PRICE_HINT =
  "Only from a column that explicitly names a maximum purchase price or a qualified amount. A self-reported budget belongs on Stated price band.";

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

export const BUYER_IMPORT_TRANSFORMS = [
  "none",
  "email",
  "phone",
  "nameCase",
  "splitFullName",
  "splitCityState",
  "stateCode",
  "money",
  "priceBand",
  "csvList",
  "dateText",
  "bool",
] as const;

export type BuyerImportTransform = (typeof BUYER_IMPORT_TRANSFORMS)[number];

export function isKnownTransform(t: string): t is BuyerImportTransform {
  return (BUYER_IMPORT_TRANSFORMS as readonly string[]).includes(t);
}

export const BUYER_IMPORT_TRANSFORM_LABELS: Record<BuyerImportTransform, string> = {
  none: "Store as written",
  email: "Email address",
  phone: "Phone number",
  nameCase: "Name casing",
  splitFullName: "Split into first + last",
  splitCityState: "Split into city + state",
  stateCode: "State code",
  money: "Money amount",
  priceBand: "Price band",
  csvList: "Split a list",
  dateText: "Date with a format",
  bool: "Yes / no",
};

/**
 * The transform applied when a target is chosen and no transform is.
 * Mirrors `BuyerImportVocabulary.DefaultTransformFor`.
 */
export function defaultTransformFor(target: string): BuyerImportTransform {
  switch (target) {
    case "Email":
      return "email";
    case "Phone":
      return "phone";
    case "FirstName":
    case "LastName":
      return "nameCase";
    case "FullName":
      return "splitFullName";
    case "CityState":
      return "splitCityState";
    case "State":
      return "stateCode";
    case "LiquidCapital":
    case "NetWorth":
    case "MaxPurchasePrice":
    case "DownPaymentAvailable":
      return "money";
    case "StatedPriceBand":
      return "priceBand";
    case "TargetIndustries":
    case "TargetGeographies":
      return "csvList";
    case "inquiry.receivedAt":
      return "dateText";
    default:
      return "none";
  }
}

/**
 * Two targets exist only to be split, and naming either without its split is a
 * plan that would silently store a whole name in the first-name column.
 * Mirrors `BuyerImportVocabulary.TransformIsLegalFor`.
 */
export function transformIsLegalFor(target: string, transform: string): boolean {
  if (target === "FullName") return transform === "splitFullName";
  if (target === "CityState") return transform === "splitCityState";
  return true;
}

/** Only the transforms a given target may legally carry. */
export function legalTransformsFor(target: string): readonly BuyerImportTransform[] {
  return BUYER_IMPORT_TRANSFORMS.filter((t) => transformIsLegalFor(target, t));
}

// ---------------------------------------------------------------------------
// Sentinels
// ---------------------------------------------------------------------------

/**
 * The built-in "this cell says nothing" values, matched case-insensitively
 * after trimming, in ADDITION to any the plan supplies.
 *
 * 🔴 Applied to contact and scalar targets ONLY — a free-text column is never
 * blanked by this list, because a message that reads "none" is a message the
 * buyer wrote.
 */
export const BUYER_IMPORT_BUILT_IN_SENTINELS = [
  "not disclosed",
  "(not disclosed)",
  "n/a",
  "na",
  "none",
  "-",
  "—",
  "unknown",
] as const;

// ---------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------

/**
 * Below this, a mapping row is highlighted.
 *
 * <p>It changes no behaviour — the plan is applied exactly as it reads. The
 * highlight is the operator's cue to look at the samples before approving,
 * which is the only defence against a confident-sounding wrong mapping.</p>
 */
export const LOW_CONFIDENCE_BELOW = 70;

export function isLowConfidence(confidence: number): boolean {
  return confidence < LOW_CONFIDENCE_BELOW;
}

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

/**
 * The sources a bulk file plausibly represents.
 *
 * <p>Deliberately not the whole `BuyerLeadSource` union —
 * `PrequalificationForm` is written by the funnel and `ManualEntry` means a
 * specialist typed it in, so offering either here would let an import mislabel
 * its own provenance. Carried over unchanged from the CSV upload this flow
 * replaces.</p>
 */
export const SMART_IMPORT_SOURCE_OPTIONS: readonly BuyerLeadSource[] = [
  "PurchasedList",
  "BusinessBroker",
  "Event",
  "Referral",
  "Organic",
  "PaidSocial",
  "PaidSearch",
  "ListingSite",
  "InboundEmail",
  "InboundCall",
  "Other",
  "Unknown",
];

/** The page's fallback when a tab carries no AI suggestion. */
export const SMART_IMPORT_DEFAULT_SOURCE: BuyerLeadSource = "PurchasedList";

/**
 * A tab's Source, from the AI's suggestion when it named one this page offers.
 *
 * <p>An unrecognised suggestion falls back rather than being forced into the
 * dropdown: a `<select>` whose value matches no option renders blank, and a
 * blank Source on a file about to be imported is worse than a stated default
 * the operator can change.</p>
 */
export function sourceFromSuggestion(
  suggestion: BuyerImportSourceSuggestionLike | null | undefined,
): BuyerLeadSource {
  const raw = suggestion?.source?.trim();
  if (!raw) return SMART_IMPORT_DEFAULT_SOURCE;
  const match = SMART_IMPORT_SOURCE_OPTIONS.find(
    (s) => s.toLowerCase() === raw.toLowerCase(),
  );
  return match ?? SMART_IMPORT_DEFAULT_SOURCE;
}

/**
 * The free text behind a tab's Source, from the same suggestion.
 *
 * <p>Unlike the enum half this is NOT validated against a list — it is
 * whatever the file said it was ("BizBuySell", a conference name, a list
 * vendor), and the point of carrying it is that `ListingSite` alone does not
 * say which site. Blank becomes null so an empty string never reaches the
 * record as a stated detail.</p>
 */
export function sourceDetailFromSuggestion(
  suggestion: BuyerImportSourceSuggestionLike | null | undefined,
): string | null {
  const raw = suggestion?.sourceDetail?.trim();
  return raw ? raw : null;
}

/** The shape the two `…FromSuggestion` helpers read, so they need no DTO import. */
export interface BuyerImportSourceSuggestionLike {
  source?: string | null;
  sourceDetail?: string | null;
}
